import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import BooksStep, {
  StepItems,
} from '../../../../../components/books/model1/BooksStep';
import * as I from '../../../../../interfaces/merchants/resources/books';

import * as Actions from '../../../../../redux/actions/merchants/resources/books';
import BookingWithPageDetail from '../common/BookingWithPageDetail';
import InputBookingTargetLesson from '../InputBookingTargetLesson';
import InputCompletionFailed from '../InputCompletionFailed';
import InputCompletionSchool from '../InputCompletionSchool';
import InputConfirmationSchool from '../InputConfirmationSchool';
import InputPayment from '../InputPayment';
import InputRequireLogin from '../InputRequireLogin';
import InputSchoolCalendar from '../InputSchoolCalendar';
import InputUserInfo from '../InputUserInfo';

interface StateProps {
  merchantResource: I.MerchantResource;
  postable: boolean;
  publicId: string;
  step: number;
  settings: I.SettingsModelTypeSchool;
}

interface DispatchProps {
  actions: {
    goTo: (step: number) => () => void;
    onSubmit: (step: number) => () => void;
    onFinalSubmit: (step: number) => () => void;
  };
}

type Props = StateProps & DispatchProps;

interface State {
  renew: boolean;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];
const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

class ModelTypeSchool extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { renew: false };
    this.handleBackToModifyPage = this.handleBackToModifyPage.bind(this);
  }

  public handleBackToModifyPage(step: number) {
    this.setState({ renew: true });
    this.props.actions.goTo(step)();
  }

  private renderRequireLoginAt(step: number) {
    return (
      <InputRequireLogin
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
      />
    );
  }

  private renderInputBookingTargetAt(step: number) {
    return (
      <BookingWithPageDetail {...this.props.merchantResource}>
        <InputBookingTargetLesson
          modelType={this.props.settings.modelType}
          renew={this.state.renew}
          settings={this.props.settings.bookingTargets}
          postable={this.props.postable}
          onSubmit={this.props.actions.onSubmit(step)}
          onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
          withExpressFlow={true}
        />
      </BookingWithPageDetail>
    );
  }

  private renderInputBookingDateTimeAt(step: number, modifierStep: number) {
    return (
      <InputSchoolCalendar
        modelType={this.props.settings.modelType}
        settings={this.props.settings.bookingDateTimePicker}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        backToModifyPage={() => this.handleBackToModifyPage(modifierStep)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
      />
    );
  }

  private renderInputUserInfoAt(step: number, submitLabelIntlId: string) {
    return (
      <InputUserInfo
        modelType={this.props.settings.modelType}
        settings={this.props.settings.userInfo}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        submitLabelIntlId={submitLabelIntlId}
      />
    );
  }

  private renderInputPaymentAt(step: number) {
    return (
      <InputPayment
        modelType={this.props.settings.modelType}
        settings={this.props.settings.payment!}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
      />
    );
  }

  private renderInputConfirmationAt(step: number) {
    return (
      <InputConfirmationSchool
        modelType={this.props.settings.modelType}
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.confirmation}
        postable={this.props.postable}
        onSubmit={this.props.actions.onFinalSubmit(step)}
        onReinputPayment={this.props.actions.goTo(ERROR_REINPUT_PAYMENT_STEP)}
        onReselect={this.props.actions.goTo(ERROR_RESELECT_STEP)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        publicId={this.props.publicId}
      />
    );
  }

  private renderInputCompletionSchool() {
    return (
      <InputCompletionSchool
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        postable={this.props.postable}
        publicId={this.props.publicId}
        withExpressFlow={true}
      />
    );
  }

  private renderFailedRestart() {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload(false)}
      />
    );
  }

  private renderFailedReselect(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reselect'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private renderFailedReinput(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reinput'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private fullWorkFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => this.renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(3, 'books.submitLabel.payment') },
      4: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => this.renderInputPaymentAt(4)         },
      5: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(5)    },
      6: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletionSchool()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReinput(4) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }

  private skipPaymentFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => this.renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(3, 'books.submitLabel.confirm') },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletionSchool()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }

  public render() {
    const [allSteps, setting] = this.props.merchantResource.paymentStep
      ? this.fullWorkFlowSetting()
      : this.skipPaymentFlowSetting();
    return (
      <>
        {setting.displayProgress && (
          <BooksStep
            allSteps={allSteps}
            current={allSteps.indexOf(setting.currentProgress) + 1}
          />
        )}
        {setting.renderFn()}
      </>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooks,
}: {
  merchantsResourcesBooks: I.State;
}): StateProps => ({
  merchantResource: merchantsResourcesBooks.merchantResource!,
  settings: merchantsResourcesBooks.settings as I.SettingsModelTypeSchool,
  postable: merchantsResourcesBooks.postable!,
  step: merchantsResourcesBooks.step!,
  publicId: merchantsResourcesBooks.merchantId!,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    goTo: (step: number) => () => Actions.goTo(step)(dispatch),
    onSubmit: (step: number) => () => Actions.nextStep(step)(dispatch),
    onFinalSubmit: (step: number) => () => Actions.finalStep(step)(dispatch),
  },
});

export default connect(mapStateProps, mapDispatchToProps)(ModelTypeSchool);
